/**
 * Prohibited-content filter (shared module).
 *
 * Single source of truth for the search-query blocklist. Both the legal
 * user-agreement component and the search controls import from here so the
 * list + match logic cannot drift apart.
 *
 * The blocklist is:
 *   - deduplicated (phrases like "bomb making" are dropped because `\bbomb\b`
 *     already matches them; explicit phrases are kept only when they add
 *     semantic clarity for admins viewing the list)
 *   - tunable at runtime via the admin API (`PUT /api/admin/prohibited-topics`)
 *
 * Matching uses word-boundary regex to avoid false positives like "certified"
 * triggering on the "ied" token.
 */

// Baked-in default used before the backend list is fetched (fast boot, safe fallback).
export const DEFAULT_PROHIBITED_TOPICS = [
  // Single-word tokens (whole-word match — NOT substring)
  'terrorism', 'terrorist', 'bomb', 'ied', 'wmd', 'sarin', 'ricin', 'anthrax',
  'botulinum', 'plutonium', 'weaponized',
  // Multi-word phrases (full phrase match, case-insensitive)
  'nuclear weapons', 'nuclear technology', 'uranium enrichment',
  'explosive devices', 'biological weapons', 'bioweapons',
  'smallpox weaponization', 'chemical weapons', 'nerve agents',
  'mustard gas', 'vx gas', 'psychological warfare', 'mind control',
  'brainwashing techniques', 'mass destruction', 'radiological weapons',
  'how to make a bomb'
];

// Current in-memory list (starts as defaults; may be replaced at runtime).
let _currentList = [...DEFAULT_PROHIBITED_TOPICS];
let _currentRegex = _compile(_currentList);

function _escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function _compile(topics) {
  if (!topics || topics.length === 0) return null;
  return new RegExp(
    `\\b(${topics.map(_escapeRegex).join('|')})\\b`,
    'i'
  );
}

/**
 * Replace the in-memory blocklist at runtime (called after the frontend fetches
 * the admin-editable list from `/api/prohibited-topics`).
 */
export function setProhibitedTopics(topics) {
  if (!Array.isArray(topics) || topics.length === 0) return;
  _currentList = topics.map(t => String(t).trim().toLowerCase()).filter(Boolean);
  _currentRegex = _compile(_currentList);
}

/** Return a shallow copy of the current list (for admin UI rendering). */
export function getProhibitedTopics() {
  return [..._currentList];
}

/** Whole-word, case-insensitive match against the current blocklist. */
export function containsProhibitedContent(query) {
  if (!query || !_currentRegex) return false;
  return _currentRegex.test(query);
}

/** User-facing message shown when content is blocked. */
export function getProhibitedContentMessage() {
  return {
    title: 'Content Not Permitted',
    message: 'InfoPilot Explorer is for Officially Approved User-Friendly Content Only. No military weapons or anything else dangerous is allowed to be researched here.',
    type: 'warning'
  };
}
